import React, {useMemo} from "react";
import BaseProductsBrowser from "@menu/components/BaseProductsBrowser";
import styled from "styled-components";
import EmptyPlaceholder from "@ui/components/EmptyPlaceholder";
import useRouter from "../../../../core/common/hooks/useRouter";
import routes from "../../../../core/common/routes";
import BaseThemeBrandHeader from "@menu/components/ThemedBrandHeader";
import {ProductFilters} from "src/core/common/hooks/useAppliedFilters";
import useBrand from "../../../../menu/hooks/useBrand";
import dynamic from "next/dynamic";
import {logModuleLoadError} from "src/core/common/utils";
import ActionsSortFilter from "src/themes/baseTheme/elements/filters/ActionsSortFilter";
import AppliedFiltersSection from "src/themes/baseTheme/elements/filters/AppliedFiltersSection";
import FilledButton from "src/core/common/components/elements/button/FilledButton";

const ProductsList = dynamic(() =>
  import("@menu/components/ProductsList").catch(
    logModuleLoadError("ProductsList", {critical: true})
  )
);

function BaseThemeBrandProductsBrowser({showFilters = false, showSort = false}) {
  return (
    <BaseProductsBrowser
      paginationStrategy={"infinite"}
      render={({manager, filters, onListUpdate}) => (
        <BrandProductsList
          manager={manager}
          filters={filters}
          onListUpdate={onListUpdate}
          showFilters={showFilters}
          showSort={showSort}
        />
      )}
    />
  );
}

function BrandProductsList({filters, onListUpdate, showSort, showFilters}) {
  const router = useRouter();
  const brandSlug = router.query["brand"];
  const [brand] = useBrand(brandSlug);

  const _filters = useMemo(() => {
    return ProductFilters.fromPrototype(filters, {brand: null});
  }, [filters]);

  return (
    <>
      {showSort && (
        <ActionsSortFilter
          filtersDisplayOptions={{brandsVisible: false}}
          filters={_filters}
        />
      )}
      {showFilters && _filters.hasFilters() && (
        <AppliedFiltersSection filters={_filters} />
      )}
      <ProductsList
        data-cy="brandProductsList"
        onListUpdate={onListUpdate}
        NoResultsPlaceHolder={NoBrandFound}
        Header={() => <BaseThemeBrandHeader brand={brand} />}
        filters={filters}
      />
    </>
  );
}

function NoBrandFound() {
  const router = useRouter();
  return (
    <EmptyPlaceholderContainer>
      <EmptyPlaceholder
        title={"Sorry, this brand is no longer available"}
        buttonText={"Check our other brands"}
        ActionsComponent={FilledButton}
        action={() => {
          router.push(routes.home);
        }}
      />
    </EmptyPlaceholderContainer>
  );
}

const EmptyPlaceholderContainer = styled.div`
  max-width: 450px;
  margin: 0 auto;
`;

export default BaseThemeBrandProductsBrowser;
